import { Link } from "react-router-dom";
import { useStickerSectionStore } from "../MugEditorFeature/EditorStore";
import { useState, useEffect } from "react";

const COLORS = {
    ink: "#1d2e2e",
    inkSoft: "#4a6160",
    teal900: "#3d5a5a",
    teal700: "#4a6a6a",
    teal600: "#5a7a7a",
    teal500: "#6b9e9e",
    teal300: "#a8d8d8",
    teal100: "#e3f3f3",
    paper: "#f6faf9",
    white: "#ffffff",
};

export default function Home() {
    const phoneModel = useStickerSectionStore((state) => state.phoneModel);

    const caseLinkTo = phoneModel ? "/editor" : "/caseconfiguration";

    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const sectionLabelStyle: React.CSSProperties = {
        display: "inline-block",
        fontSize: "0.78rem",
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: COLORS.teal500,
        marginBottom: "0.75rem",
    };

    const cardShadow = "0 1px 2px rgba(29,46,46,0.04), 0 12px 32px rgba(29,46,46,0.08)";

    const STEPS = [
        { icon: "/icons/panorama-svgrepo-com.svg", title: "Upload your photos", copy: "Drop in the pics, pet pics, memes — whatever makes it yours." },
        { icon: "/icons/slider-vertical-svgrepo-com.svg", title: "Arrange it your way", copy: "Drag, resize, and layer stickers right on the case in real time." },
        { icon: "/icons/rocket-2-svgrepo-com.svg", title: "Instant preview", copy: "See a true-to-life mockup before you ever hit checkout." },
        { icon: "/icons/delivery-svgrepo-com.svg", title: "We print & ship it", copy: "Premium printing with free shipping, right to your door." },
    ];

    const FEATURES = [
        { icon: "/icons/panorama-svgrepo-com.svg", title: "Full 3D Wrap", copy: "Edge-to-edge ink wrap for a vibrant, seamless design." },
        { icon: "/icons/sun-2-svgrepo-com.svg", title: "UV Protected", copy: "Resists outdoor fading so your design stays vivid for years." },
        { icon: "/icons/shield-up-svgrepo-com.svg", title: "Drop-Tested Durable", copy: "Shock-absorbing build protects against bumps and drops." },
        { icon: "/icons/benzene-ring-svgrepo-com.svg", title: "Soft-Touch Liner", copy: "A premium silicone inner liner cushions every impact." },
    ];

    return (
        <div style={{ backgroundColor: COLORS.paper, fontFamily: "'Quicksand', sans-serif", color: COLORS.ink, overflowX: "hidden" }}>

            {/* NAV */}
            <header
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "14px 6%",
                    backgroundColor: scrolled ? "rgba(246, 250, 249, 0.85)" : "transparent",
                    backdropFilter: scrolled ? "blur(10px)" : "none",
                    WebkitBackdropFilter: scrolled ? "blur(10px)" : "none",
                    borderBottom: scrolled ? `1px solid ${COLORS.teal100}` : "1px solid transparent",
                    transition: "all 0.25s ease",
                }}
            >
                <Link to="/" style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}>
                    <img src="/favicon.webp" alt="" style={{ width: "32px", height: "32px", borderRadius: "9px" }} />
                    <span style={{ fontSize: "1.3rem", fontWeight: 700, color: COLORS.teal900 }}>
                        Mem<span style={{ color: COLORS.teal500 }}>Case</span>
                    </span>
                </Link>

                <nav style={{ display: "flex", alignItems: "center", gap: "28px" }} className="home-nav-links">
                    <a href="#how-it-works" style={{ color: COLORS.inkSoft, textDecoration: "none", fontWeight: 600, fontSize: "0.95rem" }}>How It Works</a>
                    <Link to="/inspiration" style={{ color: COLORS.inkSoft, textDecoration: "none", fontWeight: 600, fontSize: "0.95rem" }}>Inspiration</Link>
                </nav>

                <Link to={caseLinkTo} style={{ textDecoration: "none" }}>
                    <button style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        backgroundColor: COLORS.teal900,
                        color: COLORS.white,
                        padding: "10px 20px",
                        border: "none",
                        borderRadius: "999px",
                        cursor: "pointer",
                    }}>
                        Get Started
                    </button>
                </Link>
            </header>

            {/* HERO */}
            <section style={{
                position: "relative",
                padding: "4vh 6% 6vh",
                display: "flex",
                alignItems: "center",
                gap: "4vw",
                flexWrap: "wrap",
                minHeight: "80vh",
            }}>
                <div style={{
                    position: "absolute",
                    top: "-10%",
                    right: "-10%",
                    width: "55vw",
                    height: "55vw",
                    maxWidth: "700px",
                    maxHeight: "700px",
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${COLORS.teal300}55 0%, transparent 70%)`,
                    pointerEvents: "none",
                    zIndex: 0,
                }} />

                <div style={{ flex: "1 1 420px", position: "relative", zIndex: 1, maxWidth: "640px" }}>
                    <div style={sectionLabelStyle}>Made by you</div>
                    <h1 style={{
                        fontSize: "clamp(2.2rem, 5vw, 3.6rem)",
                        lineHeight: 1.08,
                        fontWeight: 700,
                        color: COLORS.teal900,
                        margin: "0 0 20px 0",
                    }}>
                        Turn your favorite photos into a case you'll actually love
                    </h1>
                    <p style={{ fontSize: "1.15rem", color: COLORS.inkSoft, lineHeight: 1.6, marginBottom: "32px", maxWidth: "520px" }}>
                        The easiest sticker-style collage maker for phone cases.
                        Drop in your photos, arrange them your way, and we'll print and ship it to your door.
                    </p>

                    <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "36px" }}>
                        <Link to={caseLinkTo} style={{ textDecoration: "none" }}>
                            <button className="hero-cta-glow" style={{
                                fontFamily: "'Quicksand', sans-serif",
                                fontSize: "1.05rem",
                                fontWeight: 700,
                                backgroundColor: COLORS.teal500,
                                color: COLORS.white,
                                padding: "16px 30px",
                                border: "none",
                                borderRadius: "14px",
                                cursor: "pointer",
                            }}>
                                {phoneModel ? "Continue Your Case" : "Design a Phone Case"}
                            </button>
                        </Link>
                    </div>

                    <div style={{ display: "flex", gap: "22px", flexWrap: "wrap" }}>
                        {[
                            { icon: "/icons/delivery-svgrepo-com.svg", text: "Free shipping" },
                            { icon: "/icons/slider-vertical-svgrepo-com.svg", text: "60+ phone models" },
                            { icon: "/icons/magnet-svgrepo-com.svg", text: "MagSafe available" },
                        ].map((b) => (
                            <div key={b.text} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <img src={b.icon} style={{ width: "20px", height: "20px" }} />
                                <span style={{ fontSize: "0.9rem", fontWeight: 600, color: COLORS.inkSoft }}>{b.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ flex: "1 1 320px", position: "relative", zIndex: 1, display: "flex", justifyContent: "center" }}>
                    <div style={{
                        position: "relative",
                        width: "min(80vw, 380px)",
                    }}>
                        <div style={{
                            position: "absolute",
                            inset: "6%",
                            background: `linear-gradient(160deg, ${COLORS.teal500}, ${COLORS.teal300})`,
                            borderRadius: "32px",
                            filter: "blur(2px)",
                            opacity: 0.35,
                            transform: "rotate(-4deg)",
                        }} />
                        <img
                            src="/first_17_pro.webp"
                            alt="Custom phone case example"
                            style={{
                                position: "relative",
                                width: "100%",
                                display: "block",
                                borderRadius: "28px",
                                boxShadow: "0 30px 60px rgba(29,46,46,0.25)",
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* TRUST MARQUEE */}
            <div style={{ backgroundColor: COLORS.teal900, padding: "14px 0", overflow: "hidden", whiteSpace: "nowrap" }}>
                <div style={{ display: "inline-block", animation: "marquee 40s linear infinite" }}>
                    {Array.from({ length: 2 }).map((_, i) => (
                        <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                            {["Free Shipping", "60+ Phone Models", "MagSafe Available", "Starting at $34.99"].map((t) => (
                                <span key={t} style={{ color: COLORS.teal100, fontSize: "0.95rem", fontWeight: 600, margin: "0 28px" }}>
                                    {t} <span style={{ color: COLORS.teal500, marginLeft: "28px" }}>&#10022;</span>
                                </span>
                            ))}
                        </span>
                    ))}
                </div>
                <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
            </div>

            {/* HOW IT WORKS */}
            <section id="how-it-works" style={{ padding: "8vh 6%", backgroundColor: COLORS.teal100 }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <div style={sectionLabelStyle}>Dead simple</div>
                    <h2 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700, color: COLORS.teal900, margin: 0 }}>
                        From photos to doorstep in 4 steps
                    </h2>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "24px", maxWidth: "1200px", margin: "0 auto" }}>
                    {STEPS.map((s, i) => (
                        <div key={s.title} style={{ backgroundColor: COLORS.white, borderRadius: "20px", padding: "28px 22px", boxShadow: cardShadow, position: "relative" }}>
                            <div style={{
                                position: "absolute", top: "-14px", left: "22px",
                                width: "28px", height: "28px", borderRadius: "50%",
                                backgroundColor: COLORS.teal900, color: COLORS.white,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "0.85rem", fontWeight: 700,
                            }}>
                                {i + 1}
                            </div>
                            <div style={{
                                width: "52px", height: "52px", borderRadius: "14px",
                                backgroundColor: COLORS.teal100, display: "flex", alignItems: "center", justifyContent: "center",
                                marginBottom: "16px", marginTop: "10px"
                            }}>
                                <img src={s.icon} style={{ width: "26px", height: "26px" }} />
                            </div>
                            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: COLORS.teal900, margin: "0 0 8px 0" }}>{s.title}</h3>
                            <p style={{ color: COLORS.inkSoft, fontSize: "0.92rem", lineHeight: 1.5, margin: 0 }}>{s.copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURES */}
            <section style={{ padding: "8vh 6%", maxWidth: "1200px", margin: "0 auto" }}>
                <div style={{ textAlign: "center", marginBottom: "48px" }}>
                    <div style={sectionLabelStyle}>Built to last</div>
                    <h2 style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", fontWeight: 700, color: COLORS.teal900, margin: 0 }}>
                        Premium materials, every time
                    </h2>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "24px" }}>
                    {FEATURES.map((f) => (
                        <div key={f.title} style={{ padding: "26px", borderRadius: "20px", border: `1px solid ${COLORS.teal100}` }}>
                            <div style={{
                                width: "48px", height: "48px", borderRadius: "12px",
                                backgroundColor: COLORS.teal900, display: "flex", alignItems: "center", justifyContent: "center",
                                marginBottom: "16px"
                            }}>
                                <img src={f.icon} style={{ width: "24px", height: "24px", filter: "invert(1)" }} />
                            </div>
                            <h3 style={{ fontSize: "1.05rem", fontWeight: 700, color: COLORS.teal900, margin: "0 0 6px 0" }}>{f.title}</h3>
                            <p style={{ color: COLORS.inkSoft, fontSize: "0.92rem", lineHeight: 1.5, margin: 0 }}>{f.copy}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section style={{
                margin: "0 6% 8vh",
                borderRadius: "32px",
                background: `linear-gradient(135deg, ${COLORS.teal900}, ${COLORS.teal700})`,
                padding: "7vh 6%",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
            }}>
                <div style={{
                    position: "absolute", top: "-20%", left: "-10%", width: "300px", height: "300px",
                    borderRadius: "50%", background: `${COLORS.teal500}33`, pointerEvents: "none"
                }} />
                <h2 style={{ fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 700, color: COLORS.white, margin: "0 0 14px 0", position: "relative" }}>
                    Ready to make something unforgettable?
                </h2>
                <p style={{ color: COLORS.teal100, fontSize: "1.05rem", marginBottom: "32px", position: "relative" }}>
                    No account, no hassle &mdash; just upload and go.
                </p>
                <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", position: "relative" }}>
                    <Link to={caseLinkTo} style={{ textDecoration: "none" }}>
                        <button style={{
                            fontFamily: "'Quicksand', sans-serif", fontSize: "1.05rem", fontWeight: 700,
                            backgroundColor: COLORS.teal500, color: COLORS.white, padding: "16px 30px",
                            border: "none", borderRadius: "14px", cursor: "pointer",
                        }}>
                            Design a Phone Case
                        </button>
                    </Link>
                </div>
            </section>

            {/* FOOTER */}
            <footer style={{ backgroundColor: COLORS.teal900, padding: "6vh 6% 4vh" }}>
                <div style={{
                    display: "flex", flexWrap: "wrap", gap: "32px", justifyContent: "space-between",
                    maxWidth: "1200px", margin: "0 auto 32px", paddingBottom: "32px", borderBottom: `1px solid ${COLORS.teal700}`
                }}>
                    <div style={{ maxWidth: "320px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                            <img src="/favicon.webp" alt="" style={{ width: "28px", height: "28px", borderRadius: "8px" }} />
                            <span style={{ fontSize: "1.1rem", fontWeight: 700, color: COLORS.white }}>MemCase</span>
                        </div>
                        <p style={{ color: COLORS.teal300, fontSize: "0.9rem", lineHeight: 1.6 }}>
                            Custom-printed phone cases, designed by you.
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: "60px", flexWrap: "wrap" }}>
                        <div>
                            <div style={{ color: COLORS.white, fontWeight: 700, marginBottom: "12px", fontSize: "0.9rem" }}>Shop</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <Link to={caseLinkTo} style={{ color: COLORS.teal300, textDecoration: "none", fontSize: "0.9rem" }}>Phone Cases</Link>
                                <Link to="/inspiration" style={{ color: COLORS.teal300, textDecoration: "none", fontSize: "0.9rem" }}>Inspiration</Link>
                            </div>
                        </div>
                        <div>
                            <div style={{ color: COLORS.white, fontWeight: 700, marginBottom: "12px", fontSize: "0.9rem" }}>Company</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <Link to="/privacypolicy" style={{ color: COLORS.teal300, textDecoration: "none", fontSize: "0.9rem" }}>Privacy Policy</Link>
                                <Link to="/termsandconditions" style={{ color: COLORS.teal300, textDecoration: "none", fontSize: "0.9rem" }}>Terms &amp; Conditions</Link>
                                <a href="https://www.tiktok.com/@mem.case" style={{ color: COLORS.teal300, textDecoration: "none", fontSize: "0.9rem" }}>TikTok</a>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px",
                    maxWidth: "1200px", margin: "0 auto", color: COLORS.teal300, fontSize: "0.85rem"
                }}>
                    <div>&copy; {new Date().getFullYear()} MemCase. All rights reserved.</div>
                    <a href="mailto:MemCaseAssist@gmail.com" style={{ color: COLORS.teal300, textDecoration: "none" }}>MemCaseAssist@gmail.com</a>
                </div>
            </footer>

            <style>{`
                .hero-cta-glow {
                    box-shadow: 0 10px 30px rgba(107, 158, 158, 0.45);
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .hero-cta-glow:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 14px 36px rgba(107, 158, 158, 0.55);
                }
                @media (max-width: 760px) {
                    .home-nav-links { display: none !important; }
                }
            `}</style>
        </div>
    );
}
